/**
 * DIAGNOSTIC_CONTENT — binds each diagnostic-kind WindowId to its content
 * component.
 *
 * The diagnostic half of the engine's "what each window renders" layer
 * (registry.ts = metadata, WindowManagerContext = runtime state, this =
 * id→component for `kind: 'diagnostic'`). Phase 3 folded these panels off the
 * old DiagnosticsContext flags + the DiagnosticsOverlay compositor; each panel
 * now exports a CONTENT component (body only) that WindowHost wraps in the
 * shared ToolWindow chrome.
 *
 * dependency-cruiser: these components live under `ui/panels/`, inside the
 * diagnostic-ui subtree the `no-gameplay-ui-to-diagnostics` rule carves out —
 * so this map (a framework file) imports them, NOT gameplay UI. Keeping them in
 * this separate per-kind map (never the gameplay map) preserves that hard
 * boundary.
 *
 * Merged into the exhaustive `Record<WindowId, _>` by content.ts (the tsc
 * completeness guard) + pinned by the runtime completeness test.
 */

import type { WindowContentMap } from './types';
import { TargetAbilityCheckPanelContent } from '../panels/TargetAbilityCheckPanel';
import { AbilityLifecycleMonitorContent } from '../panels/AbilityLifecycleMonitor';
import { VisualLifecycleMonitorContent } from '../panels/VisualLifecycleMonitor';
import { TargetLifecycleMonitorContent } from '../panels/TargetLifecycleMonitor';
import { AbilityTestRigContent } from '../panels/AbilityTestRig';
import { AbilityGroundsPanelContent } from '../panels/AbilityGroundsPanel';
import { NetobsPacketLogPanelContent } from '../panels/NetobsPacketLogPanel';
import { NetobsLatencyHeatmapPanelContent } from '../panels/NetobsLatencyHeatmapPanel';
import { SchemaGhostToggle } from '../panels/SchemaGhostToggle';
import { RaceRunPerfPanelContent } from '../panels/RaceRunPerfPanel';

/** Diagnostic-kind id→component map. Keyed by WindowId (partial — only the 10
 *  diagnostic ids); content.ts merges the per-kind maps into the exhaustive
 *  `Record<WindowId, React.FC>`. */
export const DIAGNOSTIC_CONTENT: WindowContentMap = {
  targetAbilityCheck: TargetAbilityCheckPanelContent,
  abilityLifecycle: AbilityLifecycleMonitorContent,
  visualLifecycle: VisualLifecycleMonitorContent,
  targetLifecycle: TargetLifecycleMonitorContent,
  abilityTestRig: AbilityTestRigContent,
  abilityGrounds: AbilityGroundsPanelContent,
  netobsPacketLog: NetobsPacketLogPanelContent,
  netobsLatencyHeatmap: NetobsLatencyHeatmapPanelContent,
  schemaGhost: SchemaGhostToggle,
  raceRunPerf: RaceRunPerfPanelContent,
};
